import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import NavigationBar from '../../components/ui/NavigationBar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const PLAN_TIERS = [
  { id: 'starter', label: 'Starter', description: 'Up to 5 forms, 500 submissions/mo', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { id: 'professional', label: 'Professional', description: 'Up to 50 forms, 5,000 submissions/mo', color: 'bg-primary/10 text-primary border-primary/20' },
  { id: 'business', label: 'Business', description: 'Unlimited forms, 50,000 submissions/mo', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { id: 'enterprise', label: 'Enterprise', description: 'Custom limits, contract SLA & dedicated support', color: 'bg-violet-50 text-violet-700 border-violet-200' },
];

const ROLES = [
  {
    id: 'client_admin',
    label: 'Client Admin',
    description: 'Full access to create, edit, and manage forms within their workspace.',
    icon: 'ShieldCheck',
  },
  {
    id: 'client_viewer',
    label: 'Client Viewer',
    description: 'Read-only access to view submissions, entries, and analytics.',
    icon: 'Eye',
  },
];

const STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border border-amber-200',
  suspended: 'bg-red-50 text-red-600 border border-red-200',
};

const MOCK_WORKSPACES = [
  { id: 1, name: 'Acme Corp', email: 'admin@acmecorp.com', plan: 'professional', role: 'client_admin', status: 'active', created: 'Jul 28, 2026', forms: 12 },
  { id: 2, name: 'Bright Media', email: 'hello@brightmedia.io', plan: 'starter', role: 'client_viewer', status: 'pending', created: 'Jul 30, 2026', forms: 3 },
  { id: 3, name: 'Nova Labs', email: 'ops@novalabs.dev', plan: 'business', role: 'client_admin', status: 'active', created: 'Jul 15, 2026', forms: 27 },
];

const CreateWorkspaceModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({ name: '', email: '', plan: 'professional', role: 'client_admin' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form?.name?.trim()) e.name = 'Workspace name is required.';
    if (!form?.email?.trim()) e.email = 'Client email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) e.email = 'Enter a valid email address.';
    return e;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length) { setErrors(errs); return; }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 800));
    setLoading(false);
    onCreate({ ...form, id: Date.now(), status: 'pending', created: new Date()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), forms: 0 });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Building2" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-base font-heading font-semibold text-foreground">New Client Workspace</h2>
              <p className="text-xs text-muted-foreground">Set up an isolated workspace for your client</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-muted flex items-center justify-center transition-smooth">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Workspace Name */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Workspace Name</label>
            <Input
              placeholder="e.g. Acme Corp"
              value={form?.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e?.target?.value }))}
              className={errors?.name ? 'border-red-400' : ''}
            />
            {errors?.name && <p className="text-xs text-red-500">{errors?.name}</p>}
          </div>

          {/* Client Email */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Client Email</label>
            <Input
              type="email"
              placeholder="client@company.com"
              value={form?.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e?.target?.value }))}
              className={errors?.email ? 'border-red-400' : ''}
            />
            {errors?.email && <p className="text-xs text-red-500">{errors?.email}</p>}
            <p className="text-xs text-muted-foreground">An invitation will be sent to this address.</p>
          </div>

          {/* Plan Tier */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Plan Tier</label>
            <div className="grid grid-cols-2 gap-2">
              {PLAN_TIERS?.map((tier) => (
                <button
                  key={tier?.id}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, plan: tier?.id }))}
                  className={`text-left px-3 py-2.5 rounded-lg border-2 transition-all duration-150 ${
                    form?.plan === tier?.id
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/30' :'border-border hover:border-primary/40 bg-card'
                  }`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-sm font-semibold text-foreground">{tier?.label}</span>
                    {form?.plan === tier?.id && (
                      <Icon name="CheckCircle2" size={14} color="var(--color-primary)" />
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">{tier?.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Role Assignment */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Role Assignment</label>
            <div className="grid grid-cols-2 gap-2">
              {ROLES?.map((role) => (
                <button
                  key={role?.id}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, role: role?.id }))}
                  className={`text-left px-3 py-3 rounded-lg border-2 transition-all duration-150 ${
                    form?.role === role?.id
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/30' :'border-border hover:border-primary/40 bg-card'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name={role?.icon} size={15} color={form?.role === role?.id ? 'var(--color-primary)' : '#107570'} />
                    <span className="text-sm font-semibold text-foreground">{role?.label}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-tight">{role?.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} type="button">Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              iconName={loading ? 'Loader2' : 'Send'}
              iconPosition="left"
              type="submit"
              disabled={loading}
            >
              {loading ? 'Creating…' : 'Create & Invite'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const WorkspaceRow = ({ workspace, onDelete }) => {
  const plan = PLAN_TIERS?.find((p) => p?.id === workspace?.plan);
  const role = ROLES?.find((r) => r?.id === workspace?.role);

  return (
    <tr className="border-b border-border/60 hover:bg-muted/30 transition-smooth group">
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Building2" size={15} color="var(--color-primary)" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{workspace?.name}</p>
            <p className="text-xs text-muted-foreground">{workspace?.email}</p>
          </div>
        </div>
      </td>
      <td className="px-5 py-3.5">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${plan?.color}`}>
          {plan?.label}
        </span>
      </td>
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-1.5">
          <Icon name={role?.icon} size={13} color="#107570" />
          <span className="text-sm text-foreground">{role?.label}</span>
        </div>
      </td>
      <td className="px-5 py-3.5">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_BADGE?.[workspace?.status]}`}>
          {workspace?.status}
        </span>
      </td>
      <td className="px-5 py-3.5 text-sm text-muted-foreground">{workspace?.forms}</td>
      <td className="px-5 py-3.5 text-sm text-muted-foreground">{workspace?.created}</td>
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="w-7 h-7 rounded-md hover:bg-muted flex items-center justify-center transition-smooth" title="Edit">
            <Icon name="Pencil" size={13} color="#107570" />
          </button>
          <button
            className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center transition-smooth"
            title="Remove"
            onClick={() => onDelete(workspace?.id)}
          >
            <Icon name="Trash2" size={13} color="#ef4444" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const ClientWorkspaces = () => {
  const [showModal, setShowModal] = useState(false);
  const [workspaces, setWorkspaces] = useState(MOCK_WORKSPACES);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterRole, setFilterRole] = useState('all');

  const handleCreate = (workspace) => {
    setWorkspaces((prev) => [workspace, ...prev]);
  };

  const handleDelete = (id) => {
    setWorkspaces((prev) => prev?.filter((w) => w?.id !== id));
  };

  const filtered = workspaces?.filter((w) => {
    const matchSearch = !search || w?.name?.toLowerCase()?.includes(search?.toLowerCase()) || w?.email?.toLowerCase()?.includes(search?.toLowerCase());
    const matchPlan = filterPlan === 'all' || w?.plan === filterPlan;
    const matchRole = filterRole === 'all' || w?.role === filterRole;
    return matchSearch && matchPlan && matchRole;
  });

  const stats = [
    { label: 'Total Workspaces', value: workspaces?.length, icon: 'Building2', color: 'text-primary bg-primary/10' },
    { label: 'Active', value: workspaces?.filter((w) => w?.status === 'active')?.length, icon: 'CheckCircle2', color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Pending Invites', value: workspaces?.filter((w) => w?.status === 'pending')?.length, icon: 'Clock', color: 'text-amber-600 bg-amber-50' },
    { label: 'Total Forms', value: workspaces?.reduce((a, w) => a + w?.forms, 0), icon: 'FileText', color: 'text-violet-600 bg-violet-50' },
  ];

  return (
    <>
      <Helmet>
        <title>Client Workspaces — WebForms.biz</title>
      </Helmet>
      <NavigationBar stats={stats} />
      <div className="flex min-h-screen bg-background pt-16">
        {/* Sidebar spacer */}
        <div className="w-64 flex-shrink-0 hidden lg:block" />

        <main className="flex-1 min-w-0 px-6 py-8 max-w-6xl">
          {/* Page header */}
          <div className="flex items-start justify-between mb-8 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Building2" size={20} color="var(--color-primary)" />
                <h1 className="text-2xl font-heading font-bold text-foreground">Client Workspaces</h1>
              </div>
              <p className="text-sm text-muted-foreground max-w-xl">
                Create isolated sub-dashboards for each client. Assign plan tiers and control access with role-based permissions.
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowModal(true)}
            >
              New Workspace
            </Button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats?.map((s) => (
              <div key={s?.label} className="bg-card border border-border rounded-lg px-4 py-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s?.color}`}>
                  <Icon name={s?.icon} size={17} />
                </div>
                <div>
                  <p className="text-xl font-bold font-heading text-foreground">{s?.value}</p>
                  <p className="text-xs text-muted-foreground">{s?.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px] max-w-xs relative">
                <Icon name="Search" size={15} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <select
                value={filterPlan}
                onChange={(e) => setFilterPlan(e?.target?.value)}
                className="text-sm bg-background border border-border rounded-md px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="all">All Plans</option>
                {PLAN_TIERS?.map((t) => <option key={t?.id} value={t?.id}>{t?.label}</option>)}
              </select>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e?.target?.value)}
                className="text-sm bg-background border border-border rounded-md px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="all">All Roles</option>
                {ROLES?.map((r) => <option key={r?.id} value={r?.id}>{r?.label}</option>)}
              </select>
              <span className="ml-auto text-xs text-muted-foreground">{filtered?.length} workspace{filtered?.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Table */}
            {filtered?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Plan</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Forms</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Created</th>
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered?.map((w) => (
                      <WorkspaceRow key={w?.id} workspace={w} onDelete={handleDelete} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
                  <Icon name="Building2" size={22} color="var(--color-muted-foreground)" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">No workspaces found</p>
                <p className="text-xs text-muted-foreground mb-4">
                  {search || filterPlan !== 'all' || filterRole !== 'all' ?'Try adjusting your filters.' :'Create your first client workspace to get started.'}
                </p>
                {!search && filterPlan === 'all' && filterRole === 'all' && (
                  <Button variant="primary" size="sm" iconName="Plus" iconPosition="left" onClick={() => setShowModal(true)}>
                    New Workspace
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Role legend */}
          <div className="mt-6 bg-card border border-border rounded-lg px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Role Permissions</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ROLES?.map((role) => (
                <div key={role?.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/60">
                  <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name={role?.icon} size={14} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{role?.label}</p>
                    <p className="text-xs text-muted-foreground">{role?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
      {showModal && (
        <CreateWorkspaceModal onClose={() => setShowModal(false)} onCreate={handleCreate} />
      )}
    </>
  );
};

export default ClientWorkspaces;
